"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import AwardsView from "@/components/optilase/home/awards-view";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";
import { regionFromPathname } from "@/lib/region";
import { surfaceFrom } from "@/components/optilase/surface";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type Asset = { filename?: string; alt?: string };

type AwardLogo = SbBlokData & { name?: string; image?: Asset };

type AwardsFields = {
  title?: string;
  logos?: AwardLogo[];
  link_label?: string;
  link_url?: StoryblokLink;
};

type AwardsBlok = SbBlokData & { bg_color?: string } & SourcedBlok<AwardsFields>;

const FIELDS: (keyof AwardsFields)[] = ["title", "logos", "link_label", "link_url"];

export default function Awards({ blok }: { blok: AwardsBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const merged = mergeSourced(blok, FIELDS);
  const link = resolveLink(merged.link_url, region);
  const logos = merged.logos ?? [];

  return (
    <div {...storyblokEditable(blok)}>
      <AwardsView
        surface={surfaceFrom(blok.bg_color)}
        heading={merged.title ?? ""}
        awards={logos
          .filter((l) => l.image?.filename)
          .map((l) => ({
            image: l.image!.filename!,
            title: l.name ?? l.image?.alt ?? "",
            alt: l.image?.alt || l.name || "",
          }))}
        linkLabel={merged.link_label}
        linkHref={link.href}
      />
    </div>
  );
}
